import { ASSESSMENT_SECTIONS, RETIRED_QUESTION_IDS } from './content.js';
import { validateSection } from './validation.js';
import { clearQuestionError } from './controls.js';
import { renderWorkspace } from './workspace.js';
import { saveDraftPatch, submitAssessment } from './api.js';
import { createAutosave, SAVE_STATES } from './autosave.js';
import { createDraftQueue } from './draft-queue.js';
import '../assessment.css';

function sectionIndex(sectionId) {
  return ASSESSMENT_SECTIONS.findIndex(({ id }) => id === sectionId);
}

function sectionAnswers(section, answers) {
  return Object.fromEntries(
    section.questions
      .filter(({ id }) => Object.hasOwn(answers, id))
      .map(({ id }) => [id, answers[id]]),
  );
}

function pathSectionId() {
  const match = window.location.pathname.match(/^\/assessment\/([^/]+)\/?$/u);
  return match?.[1] || 'introduction';
}

function savedStateFromDraft(draft) {
  return draft?.updatedAt ? `Saved at ${String(draft.updatedAt).slice(11, 16)}` : SAVE_STATES.SAVED_JUST_NOW;
}

function completedBefore(index) {
  return ASSESSMENT_SECTIONS.slice(0, Math.max(0, index)).map(({ id }) => id);
}

function activeAnswers(answers) {
  return Object.fromEntries(
    Object.entries(answers ?? {}).filter(([id]) => !RETIRED_QUESTION_IDS.includes(id)),
  );
}

function sectionStatuses(answers) {
  return Object.fromEntries(ASSESSMENT_SECTIONS.map((section) => {
    const knownAnswers = sectionAnswers(section, answers);
    if (Object.keys(knownAnswers).length === 0) return [section.id, 'not_started'];
    return [section.id, validateSection(section.id, knownAnswers).valid ? 'complete' : 'attention'];
  }));
}

function assessmentReadiness(answers) {
  const statuses = sectionStatuses(answers);
  const attentionSectionCount = Object.values(statuses).filter((status) => status !== 'complete').length;
  return {
    assessmentReady: attentionSectionCount === 0,
    attentionSectionCount,
    sectionStatuses: statuses,
    submissionReadinessMessage: attentionSectionCount === 0
      ? 'All sections complete. Your assessment is ready to submit.'
      : `${attentionSectionCount} ${attentionSectionCount === 1 ? 'section needs' : 'sections need'} attention before you can submit.`,
  };
}

export async function startAssessment({
  root,
  candidate,
  draft = {},
  draftScope,
  onInvalidSession = () => {},
  onSubmitted = () => {},
} = {}) {
  const queue = createDraftQueue({ scope: draftScope });
  const restoredDraft = await queue.overlay({
    answers: {},
    currentSection: 'introduction',
    ...draft,
  });
  const hasQueuedOperations = (await queue.pending()).length > 0;
  const restoredIndex = Math.max(0, sectionIndex(restoredDraft.currentSection));
  const requestedIndex = sectionIndex(pathSectionId());
  const initialIndex = requestedIndex >= 0 && requestedIndex <= restoredIndex
    ? requestedIndex
    : restoredIndex;
  if (requestedIndex !== initialIndex) history.replaceState(null, '', `/assessment/${ASSESSMENT_SECTIONS[initialIndex].id}`);

  const state = {
    activeSectionId: ASSESSMENT_SECTIONS[initialIndex].id,
    answers: activeAnswers(restoredDraft.answers),
    candidate,
    completedSectionIds: completedBefore(restoredIndex),
    drawerOpen: false,
    errors: {},
    confirmationOpen: false,
    submissionError: '',
    saveState: hasQueuedOperations ? SAVE_STATES.SAVING : savedStateFromDraft(restoredDraft),
    unlockedSectionIndex: restoredIndex,
  };

  function viewState() {
    const readiness = assessmentReadiness(state.answers);
    return {
      ...state,
      ...readiness,
      onChange: handleChange,
      onContinue: handleContinue,
      onCloseConfirmation: closeConfirmation,
      onDrawerToggle: toggleDrawer,
      onNavigate: navigate,
      onSubmit: handleSubmit,
    };
  }

  function render({ focusHeading = false } = {}) {
    root.replaceChildren(renderWorkspace(viewState()));
    if (focusHeading) requestAnimationFrame(() => root.querySelector('h1')?.focus());
  }

  function updateSaveStatus() {
    const label = root.querySelector('.assessment-save-state span:last-child');
    if (label) label.textContent = state.saveState;
  }

  function setSaveStatus(saveState) {
    state.saveState = saveState;
    updateSaveStatus();
  }

  const autosave = createAutosave({
    savePatch: saveDraftPatch,
    queue,
    currentSection: () => state.activeSectionId,
    onStatus: setSaveStatus,
    onInvalidSession,
    onSubmitted: () => onSubmitted({ candidate, delivery: 'pending' }),
  });

  function handleChange({ questionId, value }) {
    state.answers[questionId] = value;
    if (state.errors[questionId]) {
      delete state.errors[questionId];
      clearQuestionError(root, questionId);
    }
    autosave.change(questionId, value);
    updateReviewReadiness();
  }

  function updateReviewReadiness() {
    const readiness = assessmentReadiness(state.answers);
    const submitButton = root.querySelector('[data-action="open-submission"]');
    if (submitButton) submitButton.disabled = !readiness.assessmentReady;
    const message = root.querySelector('[data-submission-readiness]');
    if (message) message.textContent = readiness.submissionReadinessMessage;
    const reviewStatus = root.querySelector('[data-review-status="review-submit"]');
    if (reviewStatus) {
      const status = readiness.sectionStatuses['review-submit'];
      reviewStatus.className = `status-${status}`;
      reviewStatus.textContent = status === 'complete' ? 'Complete' : 'Needs attention';
    }
  }

  function navigate(sectionId, { updateHistory = true, preserveErrors = false } = {}) {
    const index = sectionIndex(sectionId);
    if (index < 0 || index > state.unlockedSectionIndex) return false;
    state.activeSectionId = sectionId;
    state.drawerOpen = false;
    if (!preserveErrors) state.errors = {};
    if (updateHistory) history.pushState(null, '', `/assessment/${sectionId}`);
    render({ focusHeading: true });
    void autosave.flush();
    window.scrollTo({ top: 0, behavior: 'auto' });
    return true;
  }

  function handleContinue() {
    const index = sectionIndex(state.activeSectionId);
    const section = ASSESSMENT_SECTIONS[index];
    if (!section) return;
    if (index === ASSESSMENT_SECTIONS.length - 1) {
      if (!assessmentReadiness(state.answers).assessmentReady) return;
      state.confirmationOpen = true;
      state.submissionError = '';
      render();
      return;
    }

    if (section.id === 'introduction') {
      state.answers['introduction.acknowledged'] = true;
      autosave.change('introduction.acknowledged', true);
    }
    const validation = validateSection(section.id, sectionAnswers(section, state.answers));
    if (!validation.valid) {
      state.errors = validation.errors;
      render();
      requestAnimationFrame(() => {
        const target = root.querySelector('[aria-invalid="true"], .assessment-error');
        target?.focus?.();
        target?.scrollIntoView({ block: 'center' });
      });
      return;
    }

    if (!state.completedSectionIds.includes(section.id)) {
      state.completedSectionIds = [...state.completedSectionIds, section.id];
    }
    state.unlockedSectionIndex = Math.max(state.unlockedSectionIndex, index + 1);
    navigate(ASSESSMENT_SECTIONS[index + 1].id);
  }

  function closeConfirmation() {
    state.confirmationOpen = false;
    state.submissionError = '';
    render();
    requestAnimationFrame(() => root.querySelector('[data-action="open-submission"]')?.focus());
  }

  async function handleSubmit() {
    const errors = Object.assign({}, ...ASSESSMENT_SECTIONS.map((section) =>
      validateSection(section.id, sectionAnswers(section, state.answers)).errors,
    ));
    if (Object.keys(errors).length > 0) {
      state.errors = errors;
      state.confirmationOpen = false;
      const firstSection = ASSESSMENT_SECTIONS.find((section) =>
        section.questions.some(({ id }) => Object.hasOwn(errors, id)),
      );
      if (firstSection) navigate(firstSection.id, { preserveErrors: true });
      return;
    }

    const saved = await autosave.flush();
    if (!['saved', 'idle'].includes(saved.state)) {
      state.submissionError = 'We could not save the latest answers yet. Please try again when your work is saved.';
      render();
      return;
    }

    try {
      const response = await submitAssessment({
        schemaVersion: 1,
        operationId: globalThis.crypto.randomUUID(),
      });
      if (response.status === 200 && response.data?.state === 'submitted') {
        await autosave.clear();
        onSubmitted({ candidate, ...response.data });
        return;
      }
      if (response.status === 400 && response.data?.state === 'incomplete') {
        state.errors = response.data.errors || {};
        state.confirmationOpen = false;
        const firstSection = ASSESSMENT_SECTIONS.find((section) =>
          section.questions.some(({ id }) => Object.hasOwn(state.errors, id)),
        );
        if (firstSection) navigate(firstSection.id, { preserveErrors: true });
        return;
      }
      state.submissionError = 'We could not submit the assessment yet. Your answers are still saved.';
      render();
    } catch {
      state.submissionError = 'We could not submit the assessment yet. Your answers are still saved.';
      render();
    }
  }

  function toggleDrawer(open) {
    state.drawerOpen = Boolean(open);
    render();
    if (state.drawerOpen) {
      requestAnimationFrame(() => root.querySelector('.assessment-nav__button:not(:disabled)')?.focus());
    }
  }

  function handleWorkspaceKeydown(event) {
    if (state.confirmationOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeConfirmation();
        return;
      }
      if (event.key === 'Tab') {
        const modal = root.querySelector('.submission-modal');
        const focusable = [...(modal?.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
        ) || [])];
        const first = focusable[0];
        const last = focusable.at(-1);
        if (!modal?.contains(document.activeElement)) {
          event.preventDefault();
          first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
      return;
    }

    if (state.drawerOpen && event.key === 'Escape') {
      event.preventDefault();
      toggleDrawer(false);
      requestAnimationFrame(() => root.querySelector('.assessment-menu-button')?.focus());
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') void autosave.flush();
  }

  function handleOnline() {
    void autosave.reconnect();
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  document.addEventListener('keydown', handleWorkspaceKeydown);
  render({ focusHeading: true });
  if (hasQueuedOperations) void autosave.flush();

  return {
    navigate(sectionId, options = {}) {
      return navigate(sectionId, options);
    },
    destroy() {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('keydown', handleWorkspaceKeydown);
      autosave.destroy();
      root.replaceChildren();
    },
  };
}
